/** ReauthModal — password re-check + short-lived JWT emission. */
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import { KeyRound } from "lucide-react";
import { useUpdater } from "../../hooks/useUpdater";

export default function ReauthModal({ open, op, ref: refValue, onSubmit, onSuccess, onCancel }) {
  const { requestReauth, extractDetail } = useUpdater();
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setPassword(""); setError(null); setBusy(false); } }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    setError(null); setBusy(true);
    try {
      // Prefer parent-provided submit (so the reauth token lives in the parent's
      // useUpdater ref and is visible to installWithReauth/rollbackWithReauth).
      const result = onSubmit
        ? await onSubmit(password)
        : await requestReauth({ password, op, ref: refValue });
      setPassword("");
      onSuccess?.(result);
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 401 ? "Mot de passe incorrect." : extractDetail(err, "Erreur lors de la ré-authentification."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setPassword(""); onCancel?.(); } }}>
      <DialogContent data-testid="reauth-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Ré-authentification requise</DialogTitle>
          <DialogDescription>
            Pour cette opération sensible ({op || "?"}), saisissez à nouveau votre mot de passe administrateur.
            Le jeton d'autorisation est valable 5 minutes et à usage unique.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe administrateur"
            autoFocus
            autoComplete="current-password"
            data-testid="reauth-password"
          />
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>Annuler</Button>
            <Button type="submit" disabled={!password || busy} data-testid="btn-authorize">
              {busy ? "Vérification…" : "Autoriser"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
